import { createSelector } from 'reselect'
import { REDUX_CLASS_NAME_TO_DOMAIN } from 'modules/redux/constants'
import { NOTIFICATIONS_DOMAIN } from 'modules/notifications/constants'
import { getInstancesDomainSelector } from 'modules/redux/selectors/domain'
import getDomainMapKeyFromPathReduxUtil from 'modules/redux/utils/get-domain-map-key-from-path'
import { COMMENT_MODEL_CLASS_NAME } from 'models/daos/comment/constants'
import { compactArrayUtil, getObjectUtil } from 'packages/src'

function getUnreadNotifications (instances) {
  return instances.filter(instance => !instance.data.read)
}

function getUnreadNotificationsSelector () {
  return createSelector(
    getInstancesDomainSelector(NOTIFICATIONS_DOMAIN),
    (notifications) => getUnreadNotifications(notifications)
  )
}

/**
 * The domain where the associated instance comes from.
 * @private
 * @type {String[]}
 */
const ASSOCIATED_DOMAIN_KEYS = [
  'articles',
  'comments',
  'motivation',
  'products',
  'orders',
  'user'
]

/**
 * Gets the `Products` of the given `Notifications`.
 * @param  {Notification} notification
 * @param  {Obect} domainObj
 *         {DomainMap} articles
 *         {DomainMap} comments
 *         {DomainMap} motivation
 *         {DomainMap} products
 *         {DomainMap} orders
 *         {DomainMap} users
 * @returns {Array}
 */
function getAssociatedInstances (notification, domainObj) {
  const key = getAssociatedInstancesKey(notification)
  let associatedInstances = getAssociatedInstancesFromDomain(
    notification, key, domainObj
  )
  const firstAssociatedInstance = associatedInstances[0]
  const className = getObjectUtil(firstAssociatedInstance, 'className')
  if (className === COMMENT_MODEL_CLASS_NAME) {
    let commentAssociatedInstanceKey
    let commentAssociatedInstances
    // If it's a reply to a comment, get the reply
    const replyComment = getObjectUtil(firstAssociatedInstance, 'data.comments.0')
    if (replyComment) {
      commentAssociatedInstanceKey = getAssociatedInstancesKey(replyComment)
      commentAssociatedInstances = getAssociatedInstancesFromDomain(
        replyComment, commentAssociatedInstanceKey, domainObj
      )
    } else {
      commentAssociatedInstanceKey = getAssociatedInstancesKey(firstAssociatedInstance)
      commentAssociatedInstances = getAssociatedInstancesFromDomain(
        firstAssociatedInstance, commentAssociatedInstanceKey, domainObj
      )
    }
    const commentAssociatedUser = getAssociatedInstancesFromDomain(
      firstAssociatedInstance, 'user', domainObj
    )
    associatedInstances = [
      ...associatedInstances,
      ...commentAssociatedInstances,
      ...commentAssociatedUser
    ]
  }
  return compactArrayUtil(associatedInstances)
}

function getAssociatedInstancesKey (instance) {
  return ASSOCIATED_DOMAIN_KEYS.find(
    key => (instance.data[key] || []).length
  )
}

function getAssociatedInstancesFromDomain (instance, key, domainObj) {
  const value = instance.data[key]
  const arr = Array.isArray(value) ? value : [value]
  return compactArrayUtil(
    arr.map(
      instanceItem => {
        if (!instanceItem?.className) {
          return null
        }
        const domainName = REDUX_CLASS_NAME_TO_DOMAIN[instanceItem.className]
        const domain = domainObj[domainName]
        const domainMapKey = getDomainMapKeyFromPathReduxUtil(domainName)
        return domain.data[instanceItem.data[domainMapKey]]
      }
    )
  )
}

/**
 * Selects the `Instance`s associated to the given `Instance`.
 * @returns {Array}
 */
function getAssociatedInstancesNotificationsSelector () {
  return createSelector(
    state => state && state.articles,
    state => state && state.comments,
    state => state && state.motivation,
    state => state && state.products,
    state => state && state.orders,
    state => state && state.users,
    (state, props) => props.instance,
    (
      articles,
      comments,
      motivation,
      products,
      orders,
      users,
      notification
    ) =>
      getAssociatedInstances(
        notification, {
          articles,
          comments,
          motivation,
          products,
          orders,
          users
        }
      )
  )
}

export {
  getAssociatedInstancesNotificationsSelector,
  getUnreadNotificationsSelector
}
